// tslint:disable:no-console
import { analytics } from "@kaggle/protos";
import * as Cookie from "js-cookie";
import * as uuid from "uuid";

/**
 * Function which performs an action on events in the provided queue.
 *
 * The provided event queue will *not* be modified by this function.
 *
 * @param queue Queue containing events to be processed.
 * @param sessionInfo Information regarding current user session.
 * @param token Token used for authorization by analytics service.
 *
 * @returns Promise with response status code.
 */
export type EventQueueFlush = (
  queue: analytics.kaggle.analytics.ClientAnalyticsEvent[],
  sessionInfo: SessionInfo,
  token: string
) => Promise<number>;

/** Current session's metadata. */
export interface SessionInfo {
  userId: number | undefined;
  pageview: string;
}

/** The logic to encode the queues events and flush them to relay. */
export const prodFlushEventQueue: EventQueueFlush = async (queue, info, token) => {
  // Bypass sending events if none are enqueued.
  if (queue.length === 0) {
    return 200;
  }

  // Attempt encoding events and POST to analytics relay.
  try {
    const batch = makeEventBatch(queue, info, token);

    const body = analytics.kaggle.analytics.SaveClientAnalyticsRequest.encode(
      batch
    ).finish();

    const response = await fetch("https://analytics.kaggle.io/record-events", {
      method: "POST",
      body,
    })
    return response.status;
  } catch (err) {
    // tslint:disable-next-line:no-console
    console.warn("An issue occurred flushing the events queue.", err);
    return 500;
  }
};

/**
 * Returns a batch of events with additional metadata.
 *
 * @param queue Array of events to be logged.
 * @param sessionInfo Metadata associated with session used to augment
 *    payload containing batch of sessions.
 * @param token Token used for authorization by analytics service.
 */
function makeEventBatch(
  queue: analytics.kaggle.analytics.ClientAnalyticsEvent[],
  { userId, pageview }: SessionInfo,
  token: string
) {
  return analytics.kaggle.analytics.SaveClientAnalyticsRequest.create({
    url: document.location ? document.location.pathname : "",
    userId,
    // We no longer support kaggleAnalyticsUserHash, this should be removed
    // from the report structure in the future.
    kauh: "",
    session: getAnalyticsSessionCookie(),
    timeZoneOffsetMinutes: new Date().getTimezoneOffset(),
    referrer: document.referrer,
    token,
    performanceTier: 1,
    competitionsTier: 1,
    kernelsTier: 1,
    discussionTier: 1,
    windowWidthPixels: window.innerWidth,
    windowHeightPixels: window.innerHeight,
    screenWidthPixels: window.screen.width,
    screenHeightPixels: window.screen.height,
    events: queue,
    pageview,
    urlQueryString: window.location.search,
  });
}

/** Returns unique session identifier stored in browser's Cookies. */
function getAnalyticsSessionCookie() {
  const existingResult = getSessionCookie();
  if (existingResult) {
    return existingResult;
  }
  const newResult = uuid.v4();
  setSessionCookie(newResult);
  return newResult;
}

// getSessionCookie returns the session ID if it is present in the user's browser cookies, and
// null otherwise.
function getSessionCookie() {
  return Cookie.get("ka_sessionid");
}

function setSessionCookie(value: string) {
  // Following go/cookie-guidlines, we use a 6-month expiry for this cookie without an explicit opt-in.
  const seconds = 1000;
  const minutes = 60 * seconds;
  const hours = 60 * minutes;
  const days = 24 * hours;
  const months = 30.4 * days;
  const expiryDate = new Date();
  expiryDate.setTime(expiryDate.getTime() + 6 * months);
  document.cookie = `ka_sessionid=${value}; expires=${expiryDate.toUTCString()}; path=/`;
}

/** Writes events in queue to console. */
export const consoleFlushEventQueue: EventQueueFlush = (
  queue,
  info,
  _token
) => {
  if (queue.length > 0) {
    // Print analytics messages in a collapsed group.
    console.groupCollapsed("@kaggle/analytics", `${queue.length} events`);

    // Print session information.
    console.table([info]);

    // Print queue.
    console.table(queue);

    // End group.
    console.groupEnd();
  }

  return Promise.resolve(200);
};
